import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Video, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';

const timeSlots = [
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
];

const BookingModal = ({ isOpen, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isBooked, setIsBooked] = useState(false);

  const handleBooking = () => {
    // Mock booking - in production, this would call an API
    console.log('[MOCK] Booking scheduled:', { date: selectedDate, time: selectedTime });
    setIsBooked(true);
  };

  const resetModal = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setIsBooked(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="booking-modal"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={resetModal}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={resetModal}
              className="absolute top-4 right-4 text-[#787A7A] hover:text-[#0A0A0A] transition-colors z-10"
              data-testid="close-booking-modal-btn"
            >
              <X className="h-5 w-5" />
            </button>
            
            {isBooked ? (
              /* Success State */
              <div className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 200 }}
                  className="w-20 h-20 bg-[rgba(76,184,96,0.1)] rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle className="h-10 w-10 text-[#4CB860]" />
                </motion.div>
                
                <h2 className="text-2xl font-serif font-bold text-[#0A0A0A] mb-3">
                  Booking Confirmed!
                </h2>
                <p className="text-[#787A7A] mb-6">
                  Your FAB Diagnostic Call has been scheduled. A confirmation email will be sent shortly.
                </p>
                
                <div className="bg-[#F5F7F7] rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-4 text-[#005858]">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span className="font-medium">
                        {selectedDate?.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={resetModal}
                  className="w-full h-12 bg-[#005858] hover:bg-[#0E302A] text-white font-medium rounded-lg"
                  data-testid="close-success-btn"
                >
                  Done
                </Button>
              </div>
            ) : (
              /* Booking Form */
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-[#005858] to-[#0E302A] px-6 py-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                      <Video className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-serif font-bold text-white">
                        FAB Diagnostic Call
                      </h2>
                      <p className="text-white/80 text-sm">
                        30 minutes · Video consultation
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Calendar */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-[#787A7A] uppercase tracking-wider mb-3">
                      Select a Date
                    </h3>
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                      className="rounded-md border border-[#E2E8E8]"
                      data-testid="booking-calendar"
                    />
                  </div>
                  
                  {/* Time Slots */}
                  {selectedDate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className="mb-6"
                    >
                      <h3 className="text-sm font-medium text-[#787A7A] uppercase tracking-wider mb-3">
                        Select a Time (IST)
                      </h3>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            className={`py-3 px-4 text-sm font-medium rounded-lg border-2 transition-all ${
                              selectedTime === time
                                ? 'border-[#005858] bg-[rgba(0,88,88,0.05)] text-[#005858]'
                                : 'border-[#E2E8E8] hover:border-[#005858] text-[#0A0A0A]'
                            }`}
                            data-testid={`time-slot-${time.replace(/[:\s]/g, '-')}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Confirm Button */}
                  <Button
                    onClick={handleBooking}
                    disabled={!selectedDate || !selectedTime}
                    className="w-full h-12 bg-[#005858] hover:bg-[#0E302A] text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="confirm-booking-btn"
                  >
                    Confirm Booking
                  </Button>
                  
                  <p className="text-xs text-[#787A7A] text-center mt-4">
                    A calendar invite will be sent to your email address
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
